import type { BtlApplication } from 'api/btlApplicationForm/types';
import { firmApi } from 'api/firm/firm.api';
import { userApi } from 'api/user';
import { ooFinancingFormSchema } from 'pages/BtlApplicationFormPage/FinancingAndProperty/formSchema';
import { hydrateOoFinancingFormValues } from 'pages/BtlApplicationFormPage/FinancingAndProperty/mapApplicationToFormValues';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import { useBrokerFirm } from 'queries/broker/useBrokerFirm';
import { useAuth } from 'providers';
import { useIntl } from 'react-intl';

interface UseOoCaseFinancingFormParams {
  caseData?: BtlApplication;
  isCaseLoading: boolean;
}

export function useOoCaseFinancingForm({ caseData, isCaseLoading }: UseOoCaseFinancingFormParams) {
  const { formatMessage } = useIntl();
  const { isSignedIn } = useAuth();
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();

  if (isCaseLoading || isFirmLoading) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    firm,
    isFormEditable: false,
    isDipAcceptedOrLater: false,
    intermediaryFees: caseData?.submissionRoute?.intermediaryFees ?? [],
  };

  const formSchema = ooFinancingFormSchema(schemaProps);

  return {
    isLoading: false as const,
    formProps: {
      columns: makeFormReadonly(formSchema),
      isFormEditable: false,
      name: 'oo_financing',
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        return hydrateOoFinancingFormValues(caseData, firm, brokerReferrerId);
      },
    },
  };
}
